"use client";

import { createContext, useContext, useMemo, useState, type ReactNode } from "react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { track } from "@/lib/analytics/data-layer";
import type { AnalyticsLocation } from "@/lib/analytics/analytics-events";
import { CONCIERGE_EMAIL, CONCIERGE_WHATSAPP_HREF, CONCIERGE_WHATSAPP_LABEL } from "@/lib/concierge-contact";

/** A contact line of the dialog: the address and an arrow, hairline between, green on hover. */
const CHANNEL_ROW_CLASS =
  "flex items-center justify-between gap-4 py-[18px] text-[15px] text-[var(--fg)] transition-colors duration-[400ms] hover:text-tb-green focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tb-green focus-visible:ring-offset-2";

/**
 * The support channels, opened by the links that name them — the drawer's "Precisa de ajuda"
 * and the footer's "Precisa de Ajuda?", exactly as the reference of 2026-09-21 opens it
 * (`openHelp`). No floating button: the reference has none, and the person asked for it out on
 * the same day.
 */
type HelpDialog = { open: (location: AnalyticsLocation) => void };

const HelpDialogContext = createContext<HelpDialog | null>(null);

/**
 * The dialog's handle, for whoever can open it.
 *
 * `null` outside the provider — on the pages that share the header but not the homepage's
 * shell — and there the link keeps its own behaviour, taking the person to the section.
 */
export function useHelpDialog(): HelpDialog | null {
  return useContext(HelpDialogContext);
}

/** "Precisa de Ajuda?" — the modal and the handle its triggers use (Tech Spec § US-04). */
export function HelpModal({ children }: { children?: ReactNode }) {
  const [open, setOpen] = useState(false);

  // Reported on open only: the close is not a signal, and where it was opened from is what
  // tells the two triggers apart.
  const handle = useMemo<HelpDialog>(
    () => ({
      open: (from) => {
        setOpen(true);
        track({ event: "help_modal_open", location: from });
      },
    }),
    [],
  );

  return (
    <HelpDialogContext.Provider value={handle}>
      {children}
      {/* Only closing comes back through here: opening is the handle's job. */}
      <Dialog open={open} onOpenChange={(next) => setOpen(next)}>
      {/* Canon ORG-11, as the reference draws it: the DS dialog already carries its frame
          (min(520px,100%), fluid padding, 4px, the oxide veil). */}
      <DialogContent>
        <DialogHeader className="mb-0 gap-0">
          <DialogTitle className="text-[15px] font-medium uppercase leading-normal tracking-[0.16em] font-sans text-tb-green">
            Canais de atendimento
          </DialogTitle>
          <DialogDescription className="mt-[30px] font-display text-[clamp(22px,2.4vw,30px)] font-light leading-[1.24] tracking-[-0.01em] text-[var(--fg)] [text-wrap:pretty]">
            Uma consultora acompanha você, da escolha ao <span className="text-tb-green">embrulho</span>.
          </DialogDescription>
        </DialogHeader>
        <p className="mt-[22px] text-[15px] leading-[1.7] text-[var(--fg-muted)]">
          De segunda à quinta-feira das 8:00 às 18:00, sexta-feira das 8:00 às 17:00.
        </p>
        <div className="mt-[26px] flex flex-col gap-[2px] border-t border-[rgba(61,63,57,0.15)]">
          <a
            href={`mailto:${CONCIERGE_EMAIL}`}
            className={`${CHANNEL_ROW_CLASS} border-b border-[rgba(61,63,57,0.15)]`}
          >
            {CONCIERGE_EMAIL}
            <span aria-hidden="true" className="text-[15px]">→</span>
          </a>
          <a href={CONCIERGE_WHATSAPP_HREF} target="_blank" rel="noopener noreferrer" className={CHANNEL_ROW_CLASS}>
            WhatsApp {CONCIERGE_WHATSAPP_LABEL}
            <span aria-hidden="true" className="text-[15px]">→</span>
          </a>
        </div>
      </DialogContent>
      </Dialog>
    </HelpDialogContext.Provider>
  );
}
